import pako from 'pako';
import * as THREE from 'three';

import { ExpectedValue, MeasurementData, TargetFunction, TargetType } from '../../../store/reducers/workspace.reducer';
import { MEASUREMENT_VISUALIZATION_FIELD_MAP, MeasurementVisualizationType } from '../../../types/view';

// Field key type for measurement values
type MeasurementFieldKey = 'L' | 'E' | 'Ev' | 'Esc' | 'Ehs';

// When the scale is anchored to a norm target, the gradient max is set to
// (target * SCALE_MULTIPLIER) so the "hot" / red end means values
// significantly above the norm requirement. 4x positions:
//   1x target -> at 1/4 of the bar (brand lime, "on spec")
//   2x target -> at 1/2 of the bar (yellow, "warning")
//   3x target -> at 3/4 of the bar (orange, "high")
//   4x target -> at the end (red, "alarming")
const NORM_SCALE_MULTIPLIER = 4;

// Map field key to index in measurements array
export const FIELD_INDEX_MAP: Record<MeasurementFieldKey, number> = {
  L: 0,
  E: 1,
  Ev: 2,
  Esc: 3,
  Ehs: 4,
};

// Brand-aligned cool-to-hot gradient with stops positioned so meaningful
// multiples of the norm target line up with intuitive colors (max = 4x target):
//   0x target  -> deep navy   (under-lit / coolest)
//   1x target  -> brand lime  (perfect, at 1/4 of the bar)
//   2x target  -> warm yellow (warning, at 1/2)
//   3x target  -> orange      (high; emerges naturally between yellow & red)
//   4x target  -> red         (alarming / hottest)
// (When the scale falls back to actual data min/max, the same gradient still
// reads naturally as cool -> warm.)
const COLOR_STOPS = [
  { pos: 0, r: 31, g: 42, b: 94 }, // #1f2a5e - deep navy
  { pos: 1 / 8, r: 45, g: 179, b: 168 }, // #2db3a8 - teal (smooths navy->lime)
  { pos: 1 / 4, r: 179, g: 236, b: 73 }, // #b3ec49 - brand lime (= 1x target)
  { pos: 1 / 2, r: 245, g: 212, b: 66 }, // #f5d442 - yellow (= 2x target)
  { pos: 1, r: 230, g: 57, b: 70 }, // #e63946 - red (= 4x target)
];

// CSS gradient stops must match COLOR_STOPS exactly so the legend bar shows
// what's actually rendered in 3D.
export const GRADIENT_CSS = `linear-gradient(to right, #1f2a5e 0%, #2db3a8 12.5%, #b3ec49 25%, #f5d442 50%, #e63946 100%)`;

// Pre-instantiate THREE.Color stops once so the per-fragment lookup avoids
// re-allocating colors on every measurement point.
const THREE_COLOR_STOPS: { pos: number; color: THREE.Color }[] = COLOR_STOPS.map((s) => ({
  pos: s.pos,
  color: new THREE.Color(s.r / 255, s.g / 255, s.b / 255),
}));

/**
 * Get THREE.Color at a specific normalized position (0-1) on the gradient.
 * Stays in sync with COLOR_STOPS / GRADIENT_CSS automatically.
 */
export const getThreeColorAtPosition = (normalizedValue: number): THREE.Color => {
  const t = Math.max(0, Math.min(1, normalizedValue));
  let lower = THREE_COLOR_STOPS[0];
  let upper = THREE_COLOR_STOPS[THREE_COLOR_STOPS.length - 1];
  for (let i = 0; i < THREE_COLOR_STOPS.length - 1; i++) {
    if (t >= THREE_COLOR_STOPS[i].pos && t <= THREE_COLOR_STOPS[i + 1].pos) {
      lower = THREE_COLOR_STOPS[i];
      upper = THREE_COLOR_STOPS[i + 1];
      break;
    }
  }
  const range = upper.pos - lower.pos;
  const localT = range === 0 ? 0 : (t - lower.pos) / range;
  return new THREE.Color().lerpColors(lower.color, upper.color, localT);
};

/**
 * LRU-bounded cache of decompressed measurement payloads. The compressed
 * base64 strings are content-addressed (identical content -> identical string),
 * so they make a perfect cache key. Hits avoid two expensive operations per
 * layer per render: pako.inflate and JSON.parse.
 *
 * In practice this turns filter slider drags from "re-decompress everything
 * on every animation frame" into "decompress once per data load, never again".
 *
 * Cap is generous enough for typical projects (a few layers x positions +
 * measurements + ignoredPoints) but bounded to prevent unbounded growth.
 */
const DECOMPRESS_CACHE_MAX = 64;
const decompressCache = new Map<string, unknown>();

/**
 * Decode base64 and decompress measurement data using pako (memoized).
 */
export const decompressMeasurementData = <T>(base64Data: string): T => {
  const cached = decompressCache.get(base64Data);
  if (cached !== undefined) {
    // LRU touch: re-inserting moves the key to the end of the Map's iteration
    // order, so the oldest entry is always the eviction candidate.
    decompressCache.delete(base64Data);
    decompressCache.set(base64Data, cached);
    return cached as T;
  }

  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const decompressed = pako.inflate(bytes);
  const jsonStr = new TextDecoder('utf-8').decode(decompressed);
  const result = JSON.parse(jsonStr) as T;

  if (decompressCache.size >= DECOMPRESS_CACHE_MAX) {
    const oldest = decompressCache.keys().next().value;
    if (oldest !== undefined) decompressCache.delete(oldest);
  }
  decompressCache.set(base64Data, result);
  return result;
};

/**
 * Calculate min and max values for a specific field from raw measurement data
 */
const calculateGlobalMinMax = (
  measurementData: { positions: string; measurements: string }[] | null | undefined,
  visualizationType: MeasurementVisualizationType,
): { min: number; max: number } | null => {
  if (!measurementData || measurementData.length === 0) {
    return null;
  }

  const fieldKey = MEASUREMENT_VISUALIZATION_FIELD_MAP[visualizationType];
  if (!fieldKey) {
    return null;
  }

  const fieldIndex = FIELD_INDEX_MAP[fieldKey as MeasurementFieldKey];
  if (fieldIndex === undefined) {
    return null;
  }

  let min = Infinity;
  let max = -Infinity;

  try {
    for (const layer of measurementData) {
      if (!layer.measurements) continue;

      const measurements = decompressMeasurementData<number[][]>(layer.measurements);

      for (const measurement of measurements) {
        const value = measurement[fieldIndex];
        if (value !== -1.0 && value !== null && value !== undefined) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
    }
  } catch (error) {
    console.error('Error calculating min/max for measurement data:', error);
    return null;
  }

  if (min === Infinity || max === -Infinity) {
    return null;
  }

  return { min, max };
};

/**
 * MIN-type targets state a regulatory floor (e.g. "at least 5 lx") rather than
 * the typical operational value. Anchoring the scale directly to the floor
 * pegs everyday measurements at the red end and makes the visualization
 * useless. We assume operational lighting runs ~10x above the floor and treat
 * `MIN * 10` as the effective scale anchor instead.
 */
const MIN_TARGET_HEADROOM_MULTIPLIER = 10;

/**
 * Convert a single declared norm target into the value the gradient should be
 * anchored at. AVERAGE / EXACT / MAX targets are already operational-scale
 * numbers, so they pass through unchanged.
 */
const effectiveTargetValue = (ev: ExpectedValue): number => {
  if (ev.type === 'MIN') return ev.value * MIN_TARGET_HEADROOM_MULTIPLIER;
  return ev.value;
};

/**
 * Find the most relevant target value (norm-defined) for a given photometric
 * function on a single measurement layer. Preference order:
 *   AVERAGE > EXACT > MIN > MAX
 * — i.e. prefer concrete target values over loose bounds. The returned number
 * is the *effective* anchor (MIN already pre-scaled, see
 * {@link MIN_TARGET_HEADROOM_MULTIPLIER}).
 */
const findReferenceExpected = (expectedValues: ExpectedValue[] | undefined, fn: TargetFunction): number | null => {
  if (!expectedValues) return null;
  const order: TargetType[] = ['AVERAGE', 'EXACT', 'MIN', 'MAX'];
  for (const t of order) {
    const found = expectedValues.find((v) => v.function === fn && v.type === t);
    if (found && found.value > 0) return effectiveTargetValue(found);
  }
  return null;
};

export interface DisplayScale {
  min: number;
  max: number;
  /** When source === 'norm', `target` is the underlying norm value (max / SCALE_MULTIPLIER). */
  source: 'norm' | 'actual';
  target?: number;
}

/**
 * Compute a norm-anchored display scale for the current visualization type.
 *
 * Iterates all measurement layers, collects each layer's reference target for
 * the visualized photometric quantity, and returns
 * `{ min: 0, max: highestTarget * NORM_SCALE_MULTIPLIER }` — i.e. the legend
 * top corresponds to `NORM_SCALE_MULTIPLIER`x the strictest norm requirement.
 * Keep the multiplier in sync with the color stops in `COLOR_STOPS` (and the
 * matching `GRADIENT_CSS`) so each stop continues to land on its intended
 * target-multiple (currently 1x = lime, 2x = yellow, 4x = red).
 *
 * This intentionally ignores the actual measured values so that the scale stays
 * stable across re-renders, version switches and market-match swaps — making
 * configurations directly comparable on the same visual scale.
 *
 * Returns null when no relevant target is declared on any layer (caller
 * should fall back to an actual min/max scale).
 */
const calculateNormReferenceScale = (
  measurementData: Pick<MeasurementData, 'expectedValues'>[] | null | undefined,
  visualizationType: MeasurementVisualizationType,
): DisplayScale | null => {
  if (!measurementData || measurementData.length === 0) return null;
  const fieldKey = MEASUREMENT_VISUALIZATION_FIELD_MAP[visualizationType];
  if (!fieldKey) return null;

  let highestTarget = 0;
  let foundAny = false;
  for (const layer of measurementData) {
    const ref = findReferenceExpected(layer.expectedValues, fieldKey as TargetFunction);
    if (ref !== null) {
      foundAny = true;
      if (ref > highestTarget) highestTarget = ref;
    }
  }

  if (!foundAny || highestTarget <= 0) return null;
  return {
    min: 0,
    max: highestTarget * NORM_SCALE_MULTIPLIER,
    source: 'norm',
    target: highestTarget,
  };
};

/**
 * Whether the data has values for the quantity (its layers report an actual value for it), i.e.
 * whether points can be coloured and a scale built for it. An M-class road only has luminance, a
 * calculation area with a P norm only illuminance.
 */
export const hasVisualizationValues = (
  data: Pick<MeasurementData, 'actualValues'>[] | null | undefined,
  visualizationType: MeasurementVisualizationType | null | undefined,
): boolean => {
  const field = visualizationType ? MEASUREMENT_VISUALIZATION_FIELD_MAP[visualizationType] : null;
  if (!data || !field) return false;
  return data.some((layer) => layer.actualValues.some((a) => a.function === field && a.value != null));
};

/**
 * Resolve the scale used for coloring + legend. Prefers a norm-anchored scale
 * (stable across configurations); falls back to actual data min/max when no
 * norm target exists for the visualized quantity.
 */
export const calculateDisplayScale = (
  measurementData: MeasurementData[] | null | undefined,
  visualizationType: MeasurementVisualizationType,
): DisplayScale | null => {
  const normScale = calculateNormReferenceScale(measurementData, visualizationType);
  if (normScale) return normScale;
  const actual = calculateGlobalMinMax(measurementData, visualizationType);
  if (actual) return { ...actual, source: 'actual' };
  return null;
};

/**
 * Get the unit string for a visualization type
 */
export const getVisualizationUnit = (visualizationType: MeasurementVisualizationType): string => {
  if (visualizationType === 'luminance') {
    return 'cd/m²';
  }
  return 'lx';
};

/**
 * Generate evenly spaced tick values for a scale
 */
export const generateTicks = (min: number, max: number, numTicks: number): number[] => {
  const ticks: number[] = [];
  for (let i = 0; i <= numTicks; i++) {
    ticks.push(min + (max - min) * (i / numTicks));
  }
  return ticks;
};

/**
 * Pick a sensible number of decimals to display, based on the magnitude of
 * the range. Applied uniformly to every tick on a scale, so they line up
 * visually (e.g. "0 · 113 · 226 · 338 · 450" instead of "0.00 · 113 · ...").
 */
export const formatTicksUniform = (values: number[]): string[] => {
  const span = Math.max(...values.map(Math.abs));
  let decimals = 0;
  if (span < 1) decimals = 3;
  else if (span < 10) decimals = 2;
  else if (span < 100) decimals = 1;
  else decimals = 0;
  return values.map((v) => v.toFixed(decimals));
};
